/**
 * SortableOption — single option row in single_select / multi_select editor.
 *
 * Each option has TWO independent fields:
 *   - `option_key` (value) — stable identifier persisted in submissions and
 *     used by the form-condition engine. Sanitized to `[a-z0-9_]`. Once
 *     submissions reference an option, you should NOT change its key.
 *   - `label.fa` — display text. Free Persian. Editing the label leaves the
 *     value unchanged so condition-engine rules continue to evaluate.
 *
 * Drag-and-drop via @dnd-kit's useSortable.
 */
import { GripVertical, Trash2 } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import type { SelectOption } from '../api/form-builder-api'
import { sanitizeOptionKey } from './field-editor-helpers'
import { cn } from '@/lib/utils/cn'

interface SortableOptionProps {
  opt: SelectOption
  idx: number
  allKeys: string[]
  onChange: (patch: { option_key?: string; label?: string }) => void
  onRemove: () => void
}

export function SortableOption({
  opt, idx, allKeys, onChange, onRemove,
}: SortableOptionProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: opt.option_key })
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  }

  const duplicate = allKeys.filter((k) => k === opt.option_key).length > 1
  const empty = !opt.option_key

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="border border-border rounded-md p-2 space-y-2 bg-card/40"
    >
      <div className="flex items-start gap-2">
        <button
          {...attributes}
          {...listeners}
          className="cursor-grab text-muted-foreground hover:text-foreground pt-2"
          aria-label="مرتب‌سازی"
        >
          <GripVertical className="w-3.5 h-3.5" />
        </button>
        <div className="flex-1 space-y-1">
          <Label className="text-[10px] text-muted-foreground">برچسب</Label>
          <Input
            value={opt.label.fa}
            onChange={(e) => onChange({ label: e.target.value })}
            placeholder={`برچسب گزینه ${idx + 1}`}
            className="h-7 text-xs"
          />
        </div>
        <button
          onClick={onRemove}
          className="text-muted-foreground hover:text-destructive pt-7"
          aria-label="حذف گزینه"
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>

      <div className="ps-6 space-y-1">
        <Label className="text-[10px] text-muted-foreground flex items-center gap-1">
          مقدار
          <span className="text-[9px] text-muted-foreground/60" dir="ltr">(a-z, 0-9, _)</span>
        </Label>
        <Input
          value={opt.option_key}
          onChange={(e) => onChange({ option_key: sanitizeOptionKey(e.target.value) })}
          placeholder="value_key"
          className={cn(
            'h-7 text-xs font-mono',
            (duplicate || empty) && 'border-destructive',
          )}
          dir="ltr"
          aria-invalid={duplicate || empty || undefined}
        />
        {empty && (
          <p className="text-[10px] text-destructive">مقدار نمی‌تواند خالی باشد</p>
        )}
        {duplicate && !empty && (
          <p className="text-[10px] text-destructive">این مقدار با گزینه دیگری تکراری است</p>
        )}
      </div>
    </div>
  )
}
